<template>
  <div class="login-form">
    <h1 class="form-title">{{ t('auth.login.title') }}</h1>

    <form class="form-container" @submit.prevent="handleLogin">
      <!-- Email Input -->
      <div class="form-group">
        <label for="email" class="form-label">{{ t('auth.login.label.email') }}</label>
        <input
          id="email"
          name="email"
          v-model="formData.email"
          type="text"
          :class="['form-control', { 'form-control-error': errors.email || apiError }]"
          :placeholder="t('form.placeholder.email')"
          @input="validateEmail"
        />
        <p :class="['error-message', { 'error-visible': errors.email }]">{{ errors.email || '&nbsp;' }}</p>
      </div>

      <!-- Password Input -->
      <div class="form-group">
        <label for="password" class="form-label">{{ t('auth.login.label.pass') }}</label>
        <div class="password-wrapper">
          <input
            id="password"
            name="password"
            v-model="formData.password"
            :type="showPassword ? 'text' : 'password'"
            :class="['form-control', { 'form-control-error': errors.password || apiError }]"
            placeholder="********"
            @blur="validatePassword"
            @input="validatePassword"
          />
          <button type="button" class="password-toggle" @click="showPassword = !showPassword">
            <svg v-if="!showPassword" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path><circle cx="12" cy="12" r="3"></circle></svg>
            <svg v-else xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path><line x1="1" y1="1" x2="23" y2="23"></line></svg>
          </button>
        </div>
        <p :class="['error-message', { 'error-visible': errors.password }]">{{ errors.password || '&nbsp;' }}</p>
      </div>

      <!-- API Error Message -->
      <div v-if="apiError" class="alert alert-danger">
        {{ t('apiCodes.' + apiError) }}
      </div>

      <!-- Submit Button -->
      <button
        type="submit"
        class="btn-login"
        :disabled="isLoading || cooldown > 0"
      >
        <span v-if="isLoading" class="spinner"></span>
        {{ isLoading ? t('header.user.logging_in') : (cooldown > 0 ? `${t('auth.login.btn')} (${cooldown}s)` : t('auth.login.btn')) }}
      </button>

      <!-- Legal notice for reCAPTCHA -->
      <div class="recaptcha-notice" v-html="t('form.recaptcha_notice', { 
        privacy: `<a href='https://policies.google.com/privacy' target='_blank'>${t('form.google_privacy')}</a>`, 
        terms: `<a href='https://policies.google.com/terms' target='_blank'>${t('form.google_terms')}</a>` 
      })"></div>
    </form>

    <!-- Footer Links -->
    <div class="form-footer">
      <router-link to="/register" class="link-register">{{ t('auth.login.register_link') }}</router-link>
      <router-link to="/forgot-password" class="link-forgot">{{ t('auth.login.forgot_link') }}</router-link>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, onUnmounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/authStore'
import { useConfigStore } from '@/stores/configStore'
import { useLanguage } from '@/composables/useLanguage'
import { useToast } from '@/composables/useToast'

const { t, currentLanguage } = useLanguage()
const configStore = useConfigStore()

// Re-validate on language change to update error messages
watch(currentLanguage, () => {
  if (errors.value.email) validateEmail()
  if (errors.value.password) validatePassword()
})
const toast = useToast()
const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()
const isLoading = ref(false)
const showPassword = ref(false)
const error = ref('')
const cooldown = ref(0)
let cooldownTimer = null

const startCooldown = (seconds) => {
  cooldown.value = seconds
  if (cooldownTimer) clearInterval(cooldownTimer)
  cooldownTimer = setInterval(() => {
    cooldown.value--
    if (cooldown.value <= 0) {
      clearInterval(cooldownTimer)
    }
  }, 1000)
}

onUnmounted(() => {
  if (cooldownTimer) clearInterval(cooldownTimer)
})

const formData = ref({
  email: '',
  password: ''
})

const errors = ref({
  email: '',
  password: ''
})

const apiError = ref('')

const validateEmail = () => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  if (!formData.value.email) {
    errors.value.email = t('form.error.required')
  } else if (!emailRegex.test(formData.value.email)) {
    errors.value.email = t('form.error.email_invalid')
  } else {
    errors.value.email = ''
  }
}

const validatePassword = () => {
  if (!formData.value.password) {
    errors.value.password = t('form.error.required')
  } else if (formData.value.password.length < 8) {
    errors.value.password = t('form.error.min_length', { count: 8 })
  } else {
    errors.value.password = ''
  }
}

const generateRecaptchaToken = async () => {
  if (!configStore.recaptchaSiteKey) return ''
  
  const waitForRecaptcha = () => {
    return new Promise((resolve) => {
      if (window.grecaptcha && window.grecaptcha.enterprise) {
        resolve(true)
        return
      }
      
      let attempts = 0
      const interval = setInterval(() => {
        attempts++
        if (window.grecaptcha && window.grecaptcha.enterprise) {
          clearInterval(interval)
          resolve(true)
        } else if (attempts > 50) {
          clearInterval(interval)
          resolve(false)
        }
      }, 100)
    })
  }

  const isRecaptchaReady = await waitForRecaptcha()
  if (!isRecaptchaReady) return ''

  return await new Promise((resolve) => {
    window.grecaptcha.enterprise.ready(async () => {
      try {
        const token = await window.grecaptcha.enterprise.execute(configStore.recaptchaSiteKey, { action: 'login' })
        resolve(token)
      } catch (e) {
        console.error('[RECAPTCHA] Execute error:', e)
        resolve('')
      }
    })
  })
}

const handleLogin = async () => {
  validateEmail()
  validatePassword()

  if (errors.value.email || errors.value.password) {
    return
  }

  apiError.value = ''
  isLoading.value = true

  try {
    const recaptchaToken = await generateRecaptchaToken()
    if (!recaptchaToken) {
      apiError.value = 'AUTH_RECAPTCHA_REQUIRED'
      isLoading.value = false
      return
    }
    const result = await authStore.login(formData.value.email, formData.value.password, recaptchaToken)
    
    if (result && result.success) {
      toast.success(t('toast.success.login'))
      const redirectPath = route.query.redirect
      
      if (redirectPath) {
        if (redirectPath.includes('carrello')) {
          router.replace({ name: 'Cart' })
        } else if (redirectPath.includes('account')) {
          router.replace({ name: 'Account' })
        } else {
          const cleanPath = redirectPath.startsWith('/') ? redirectPath : `/${redirectPath}`
          router.replace(cleanPath)
        }
      } else {
        router.replace({ name: 'Home' })
      }
    } else {
      const code = result?.code || 'AUTH_INVALID_CREDENTIALS'
      apiError.value = code
      if (code === 'AUTH_RATE_LIMITED' || code === 'AUTH_IP_LOCKED') {
        startCooldown(30)
      } else if (code === 'AUTH_ACCOUNT_LOCKED') {
        startCooldown(60)
      }
    }
  } catch (err) {
    const code = err?.response?.data?.code || 'AUTH_INVALID_CREDENTIALS'
    apiError.value = code
    if (code === 'AUTH_RATE_LIMITED' || code === 'AUTH_IP_LOCKED') {
      startCooldown(30)
    } else if (code === 'AUTH_ACCOUNT_LOCKED') {
      startCooldown(60)
    }
  } finally {
    isLoading.value = false
  }
}
</script>

<style scoped>
.login-form {
  position: relative;
  z-index: 10;
}

.form-title {
  text-align: center;
  font-family: 'Montserrat', sans-serif;
  font-size: 20px;
  font-weight: 800;
  letter-spacing: 0.5px;
  color: #000000;
  margin-bottom: 28px;
  text-transform: uppercase;
}

.form-group {
  margin-bottom: 20px;
}

.form-label {
  display: block;
  font-family: 'Montserrat', sans-serif;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.8px;
  color: #444444;
  margin-bottom: 8px;
  text-transform: uppercase;
}

.form-control {
  width: 100%;
  padding: 13px 14px;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  color: #000000;
  border: 1.5px solid #e5e5e5;
  border-radius: 6px;
  background-color: #ffffff;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.form-control:focus {
  outline: none;
  border-color: var(--dark-teal);
  box-shadow: 0 0 0 4px rgba(0,140,149,0.12);
}

.form-control::placeholder {
  color: #bbbbbb;
}

.form-control:hover {
  border-color: #cccccc;
}

.form-control-error {
  border-color: #d9534f !important;
}

.password-wrapper {
  position: relative;
  width: 100%;
}

.password-toggle {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  background: transparent !important;
  border: none !important;
  outline: none !important;
  cursor: pointer;
  color: #888888;
  padding: 0;
  margin: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s ease;
  height: 100%;
}

.password-toggle:hover {
  color: var(--dark-teal);
}

.password-toggle svg {
  display: block;
}

.error-message {
  font-family: 'Inter', sans-serif;
  font-size: 11px;
  color: #d9534f;
  margin-top: 6px;
  font-weight: 600;
  height: 14px;
  opacity: 0;
  transition: opacity 0.3s ease;
  letter-spacing: 0.3px;
}

.error-message.error-visible {
  opacity: 1;
}

.alert {
  padding: 14px;
  margin-bottom: 24px;
  font-family: 'Montserrat', sans-serif;
  font-size: 12px;
  font-weight: 700;
  border: 1px solid #d9534f;
  color: #d9534f;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  text-align: center;
  background: rgba(217, 83, 79, 0.05);
  border-radius: 6px;
}

.btn-login {
  width: 100%;
  padding: 15px;
  font-family: 'Montserrat', sans-serif;
  font-size: 14px;
  font-weight: 700;
  color: #ffffff;
  background: #000000;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  text-transform: uppercase;
  letter-spacing: 1px;
  transition: background 0.2s ease, transform 0.15s ease, box-shadow 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 6px;
}

.btn-login:hover:not(:disabled) {
  background: var(--dark-teal);
  box-shadow: 0 8px 20px -6px rgba(0,140,149,0.5);
}

.btn-login:active:not(:disabled) {
  transform: translateY(1px);
}

.btn-login:disabled {
  background: #5C8889;
  opacity: 0.5;
  cursor: not-allowed;
}

.spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top: 2px solid white;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.recaptcha-notice {
  text-align: center;
  font-size: 11px;
  line-height: 18px;
  color: #888888;
  margin: 16px 0 22px;
}

.recaptcha-notice a {
  color: #888888;
  text-decoration: underline;
  text-underline-offset: 2px;
}

.recaptcha-notice a:hover {
  color: var(--dark-teal);
}

.form-footer {
  margin-top: 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  text-align: center;
}

.link-register {
  font-family: 'Montserrat', sans-serif;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  color: var(--dark-teal);
  text-decoration: none;
  position: relative;
  padding-bottom: 2px;
  transition: color 0.2s ease;
}

.link-register::after {
  content: '';
  position: absolute;
  left: 0; right: 0; bottom: -1px;
  height: 1.5px;
  background: var(--bright-teal);
  transform: scaleX(0);
  transform-origin: center;
  transition: transform 0.25s ease;
}

.link-register:hover {
  color: var(--bright-teal);
}

.link-register:hover::after {
  transform: scaleX(1);
}

.link-forgot {
  font-family: 'Montserrat', sans-serif;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  color: #888888;
  text-decoration: none;
  transition: color 0.2s ease;
}

.link-forgot:hover {
  color: #000000;
}

@media (max-width: 600px) {
  .form-title {
    font-size: 18px;
    margin-bottom: 24px;
  }

  .btn-login {
    padding: 14px;
    font-size: 13px;
  }
}
</style>
